import { lazy, Suspense, useEffect, useMemo, useState } from "react";
import { Link, Navigate, useParams, useSearchParams } from "react-router-dom";
import * as api from "../api";
import { ScenaGenerare } from "../components/Imprimanta";
import { useRolProiect } from "../components/rol";
import Vizualizator, { tintaFromParam } from "../viewer/Vizualizator";
import { instanceKey } from "../viewer/validation";
import type { VAnno, VInstance, VStatus, ValidationReport } from "../viewer/validation";
import ObsHost from "../structure/ObsHost";
import { useStructure } from "../structure/StructureContext";
import { importulAPicat, stareMachetaSituatie } from "./generare";
import BlocSolutie, { EroareRegistru, EroareSolutie } from "../nav/BlocSolutie";
import PlanseSchimbate from "../nav/PlanseSchimbate";
import { useRegistru } from "../nav/Registru";
import { useActiuni } from "../nav/actiuni";
import { useFir } from "../nav/useFir";
import type { Disciplina } from "../nav/api";
import { caleEtapa, caleRand, eDeschis, implicitaPe, numeEtapa, numeRand,
         notaRulare } from "../nav/selectie";
import { useT, useTx } from "../i18n";

// VIZUALIZATORUL unei discipline: planșa pe tot ecranul, cu UN SINGUR meniu
// plutitor în stânga: spațiul de pe ecran e al planșei, nu al unei liste de
// soluții (v. ui/docs/arhitectura_informatiei.md, «Corecția»). Deasupra
// ei stau doar antetul, cu lanțul, și șina. Tab-urile de disciplină care
// pluteau aici și cipurile «pe X» au plecat: disciplina e a RUTEI, iar pe ce
// stă soluția o spune lanțul.
//
// Ruta poartă un singur id — rândul privit (contract §8):
//   /p/:id/situatie/:sol · /p/:id/arhitectura/:sol · /p/:id/structura/:sol
// Fără id (segmentul gol, eventual `?pe=<amonte>`), se deschide soluția
// implicită de pe amonte (`registru.implicit`), iar dacă nu e niciuna, ecranul
// spune «nimic pe A1», cu «Generează soluția» — abia după o citire proaspătă a
// registrului: ecranul pe care tocmai a pornit o generare ajunge aici înaintea
// sondajului. Un id necunoscut, sau al altei discipline, se spune, cu drum spre
// soluția implicită (Î24) — tot după o citire proaspătă (v. `useFir`). Un
// registru care n-a putut fi citit deloc se spune și el, cu «Încearcă din nou».
//
// Nicio etapă nu mai e dezactivată (A9): o soluție în lucru se deschide pe
// ce are deja — arhitectura cu etajele scrise, structura pe «Rezumat».

const StructureStudio = lazy(() => import("../structure/Studio"));

export default function Studio({ disciplina }: { disciplina: Disciplina }) {
  const { projectId = "" } = useParams();
  const [params] = useSearchParams();
  const { registru, eroare: eroareRegistru } = useRegistru();
  const { ruta, rand, eroare, fir, incarcat } = useFir();
  const structura = useStructure();
  const t = useT();

  // Elementul cerut prin link — de pe ecranul de Verificări, „vezi pe planșă".
  // Stă în URL din același motiv ca soluția: un link către «holul care
  // picase» trebuie să poată fi trimis cuiva.
  const tinta = tintaFromParam(params.get("sel"));
  const regulaCeruta = params.get("rule");
  const instantaCeruta = params.get("inst");

  // Desenul validatorului, adus pentru instanța cerută în link.
  //
  // Nu poate călători prin URL — o cotă are coordonate, un traseu are zeci — deci
  // se cere de la validator regula anume (`?rules=`), exact cererea pe care o
  // face și ecranul de Verificări, și se caută instanța după aceeași cheie.
  const [adnotare, setAdnotare] = useState<{ anno: VAnno; status: VStatus } | null>(null);
  const idRand = rand?.id ?? null;
  useEffect(() => {
    setAdnotare(null);
    if (!regulaCeruta || !instantaCeruta || !idRand || disciplina === "structure") return;
    let renunta = false;
    api.post<ValidationReport>(
      `/api/projects/${projectId}/solutions/${idRand}/verificare`
      + `?rules=${encodeURIComponent(regulaCeruta)}`)
      .then((r) => {
        if (renunta) return;
        const inst = (r.rules[0]?.instances ?? [])
          .find((i: VInstance) => instanceKey(i) === instantaCeruta);
        if (inst?.anno) setAdnotare({ anno: inst.anno, status: inst.status });
      })
      .catch(() => undefined);   // planul rămâne desenat, doar fără explicație
    return () => { renunta = true; };
  }, [projectId, idRand, regulaCeruta, instantaCeruta, disciplina]);

  // Silueta clădirii, pentru scheletul care ține locul desenului cât se
  // încarcă: a arhitecturii firului, altfel a primei gata. Vine cu registrul
  // (`summary.silueta`), deci nu costă o cerere în plus.
  const silueta = useMemo(() => {
    const arh = (registru?.solutii ?? []).filter(
      (s) => s.discipline === "architecture" && s.status === "ready");
    const aleasa = arh.find((s) => s.id === fir.architecture) ?? arh.find((s) => s.promoted)
      ?? arh[0];
    return aleasa?.summary.silueta ?? null;
  }, [registru, fir.architecture]);

  if (!registru && eroareRegistru) {
    return <div className="studio"><EroareRegistru motiv={eroareRegistru} /></div>;
  }
  if (!registru || !incarcat) {
    return <div className="studio"><p className="note float" style={{ left: 16, top: 16 }}>
      {t("comun.se_incarca")}</p></div>;
  }

  // --- segmentul gol: soluția implicită de pe amonte ------------------------
  if (!ruta.sol) {
    const amonteDisc: Disciplina | null = disciplina === "architecture" ? "site_plan"
      : disciplina === "structure" ? "architecture" : null;
    const pe = ruta.pe ?? (amonteDisc ? fir[amonteDisc] : null);
    // Regula e a serverului; pe un proiect care abia pornește ea n-are încă
    // ce alege (nimic `ready`), iar atunci se deschide rândul care se
    // generează — pe panoul lui de așteptare, nu pe «nimic».
    const inLucru = registru.solutii
      .filter((s) => s.discipline === disciplina && eDeschis(s.stare)
        && (disciplina === "site_plan"
            || (disciplina === "architecture" ? s.lineage.site_plan === pe
                : s.lineage.architecture === pe)))
      .sort((a, b) => a.numar - b.numar)[0];
    // Fără amonte — proiectul de structură, fără plan de situație, sau un
    // import care nu stă pe nicio amplasare — cheia «pe X» n-are ce căuta:
    // se deschide firul implicit al proiectului. Altfel importul încheiat
    // lăsa ecranul pe «nicio arhitectură», deși rândul lui era gata.
    const tintaId = (disciplina === "site_plan" ? implicitaPe(registru, "site_plan")
      : pe ? implicitaPe(registru, disciplina, pe)
      : registru.implicit.fir[disciplina]) ?? inLucru?.id ?? null;
    if (tintaId) {
      return <Navigate replace to={caleRand(projectId, { id: tintaId, discipline: disciplina },
                                             { vedere: ruta.vedere })} />;
    }
    return <Nimic disciplina={disciplina} pe={pe} />;
  }

  if (eroare || !rand) {
    const implicita = caleEtapa(projectId, disciplina, fir[disciplina],
      disciplina === "structure" ? fir.architecture
        : disciplina === "architecture" ? fir.site_plan : null);
    return (
      <div className="studio">
        <EroareSolutie fel={eroare ?? "necunoscuta"} href={implicita} />
      </div>
    );
  }

  // --- structura: vizualizatorul ei, cu meniul lui ---------------------------
  if (disciplina === "structure") {
    if (structura.eroare) {
      return (
        <div className="studio">
          <EroareSolutie fel={structura.eroare}
                         href={caleRand(projectId, rand)} />
        </div>
      );
    }
    return (
      <ObsHost className="ob-main ob-main-plin">
        <Suspense fallback={<div className="ob-pagina sg-muted">
                              {t("studio.se_incarca_modelul")}
                            </div>}>
          <StructureStudio
            id={projectId}
            cloneId={structura.cloneId}
            solutie={structura.solutie}
            steps={structura.steps}
            rezumate={structura.rezumate}
            silueta={silueta}
          />
        </Suspense>
      </ObsHost>
    );
  }

  // --- planul de situație și arhitectura --------------------------------------
  // Un rând fără document încă (la rând, sau picat înainte să scrie ceva) nu
  // are ce desena. Cât e deschis, ecranul e al scenei de generare, singură:
  // meniul soluției ar fi doar «Soluția 1, în lucru» spus încă o dată (Stefan,
  // 13.10.2026). Picat, rămâne meniul — cu starea și acțiunile lui.
  const areDesen = rand.status === "ready" || rand.status === "running"
    || rand.status === "nonconvergent";
  return (
    <div className="studio">
      {areDesen && (
        <Vizualizator projectId={projectId} solution={rand} plin editabil
                      tinta={tinta} adnotare={adnotare}
                      silueta={silueta}
                      stareMacheta={rand.discipline === "site_plan"
                        ? stareMachetaSituatie(rand, registru.solutii,
                                               registru.rulari_fara_solutie)
                        : undefined}
                      blocSolutie={<BlocSolutie rand={rand} />} />
      )}
      {!areDesen && (eDeschis(rand.stare) ? (
        <div className="studio-asteapta">
          <ScenaGenerare stinsa={rand.stare !== "in_lucru"}
                         titlu={t(rand.rulare?.kind === "external_architecture"
                           ? "studio.gen.architecture_import" : `studio.gen.${disciplina}` as const)}
                         nota={notaRulare(rand.rulare)} />
        </div>
      ) : (
        <div className="float coloana-stanga" style={{ left: 16, top: 16 }}>
          <div className="card bara-vederi meniu-stanga">
            <BlocSolutie rand={rand} />
          </div>
        </div>
      ))}
    </div>
  );
}

/** «Nimic pe A1»: segmentul gol, fără soluție implicită. Cât vine ceva — o
 *  rulare fără rând încă —, scena de generare; altfel ce lipsește și,
 *  la structură, «Generează soluția» (contract §5.1: pornește și schița, dacă
 *  lipsește). */
function Nimic({ disciplina, pe }: { disciplina: Disciplina; pe: string | null }) {
  const t = useT();
  const tx = useTx();
  const { registru, projectId, reciteste } = useRegistru();
  const { potScrie } = useRolProiect();
  const actiuni = useActiuni();
  const [jobs, setJobs] = useState<api.Job[]>([]);
  // «Nimic» se spune abia după o citire proaspătă: pornirea care a dus aici
  // (tema, importul din dosar) a creat o rulare pe care registrul ținut încă
  // n-o are. Dacă citirea aduce ceva, segmentul gol se deschide singur pe el.
  const [proaspat, setProaspat] = useState(false);
  useEffect(() => {
    let viu = true;
    void reciteste().finally(() => { if (viu) setProaspat(true); });
    return () => { viu = false; };
  }, [reciteste]);
  useEffect(() => {
    if (disciplina !== "architecture") return;
    let viu = true;
    api.listJobs(projectId).then((j) => viu && setJobs(j)).catch(() => undefined);
    return () => { viu = false; };
  }, [projectId, disciplina]);
  if (!registru) return null;
  const amonte = pe ? registru.solutii.find((s) => s.id === pe) ?? null : null;
  const venite = registru.rulari_fara_solutie.filter((j) =>
    (disciplina === "site_plan" && j.kind === "site_plan")
    || (disciplina === "architecture"
        && (j.kind === "architecture" || j.kind === "external_architecture")
        && (!pe || j.upstream_solution_id === pe)));
  const vine = venite.length > 0;
  // Importul nu generează arhitectura, o citește din planșe.
  const seImporta = venite.length > 0 && venite.every((j) => j.kind === "external_architecture");
  if (!vine && !proaspat) {
    return <div className="studio"><p className="note float" style={{ left: 16, top: 16 }}>
      {t("comun.se_incarca")}</p></div>;
  }
  const importPicat = disciplina === "architecture" ? importulAPicat(registru.solutii, jobs) : null;

  if (vine) {
    return (
      <div className="studio">
        <div className="studio-asteapta">
          <ScenaGenerare titlu={t(seImporta ? "studio.gen.architecture_import"
            : `studio.gen.${disciplina}` as const)} />
        </div>
      </div>
    );
  }
  return (
    <div className="studio">
      <div className="float card" style={{ left: 16, top: 16, padding: "16px 18px", maxWidth: 440 }}>
        {importPicat ? (
          <>
            <b className="bad">{t("studio.import_picat")}</b>
            <p className="note" style={{ marginTop: 6 }}>
              {importPicat.error?.message
                || t(importPicat.status === "cancelled" ? "studio.import_anulat"
                     : importPicat.is_dead ? "studio.import_fara_puls"
                     : "studio.import_picat_text")}
            </p>
            <Link className="btn" style={{ marginTop: 10 }} to={`/p/${projectId}/dossier`}>
              {t("studio.import_inapoi")}
            </Link>
          </>
        ) : (
          <>
            <b>
              {amonte
                ? t("studio.fara_pe", { disciplina: numeEtapa(disciplina).toLowerCase(),
                                        solutie: numeRand(amonte) })
                : t("studio.fara_solutie", { disciplina: numeEtapa(disciplina).toLowerCase() })}
            </b>
            <p className="note" style={{ marginTop: 6 }}>{tx("studio.fara_solutie_text")}</p>
            {disciplina === "structure" && <PlanseSchimbate projectId={projectId} rand={amonte} />}
            {disciplina === "structure" && amonte && potScrie && (
              <button className="btn btnp" style={{ marginTop: 10 }}
                      disabled={!!actiuni.ocupat}
                      onClick={() => actiuni.genereazaSolutia(amonte.id)}>
                {t("actiune.genereaza")}
              </button>
            )}
            {actiuni.eroare && <p className="note bad" style={{ marginTop: 6 }}>{actiuni.eroare}</p>}
          </>
        )}
      </div>
    </div>
  );
}
